import {
  Box,
  Button,
  Checkbox,
  Grid,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Custom3dotString from "../../../../../components/Custom3dotString";
import CustomButton from "../../../../../components/CustomButton";
import CustomModal from "../../../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../../../components/CustomTextField";
import SearchableSelect from "../../../../../components/Select/SearchableSelect";
import CustomSelect from "../../../../../components/Select/Select";
import {
  getPetsCompliantSummaryWoLoader,
  updateProcedureByIdWoLoader,
} from "../../../../../redux/reducers/petSlice";
import { showSnackBar } from "../../../../../redux/reducers/snackSlice";
import { AppColors } from "../../../../../util/AppColors";
import { routeList } from "../../../../../util/dropList";
import { billingTabEnum } from "../../../../../util/enum";
import {
  getDoneAtFromNextDueDate,
  getRightPackageUom,
  isBatchAllowedForFacility,
} from "../../../../../util/function";
import { refreshClinicStocksToSession } from "../../../../../util/refreshClinicStocksToSession";

const NOT_LINKED_TO_PROCEDURE = "Not linked to a procedure";

const initValues = {
  plName: null,
  stockId: null,
  batchNumber: null,
  batchId: null,
  quantity: 1,
  uom: null,
  dosage: "",
  route: null,
  linkToProcedure: NOT_LINKED_TO_PROCEDURE,
  serviceId: null,
  variantId: null,
  billedSeparately: false,
  nameErr: false,
};

const AddAdministerDrawer = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const [values, setValues] = useState(initValues);
  const [selectedItems, setSelectedItems] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const reqDet = useSelector((state) => state?.pet?.complaintSummary?.data);
  const appDet = reqDet?.appointment;
  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const { clinicStocks, clinicDet, services, locations, facilities } =
    useMemo(() => {
      if (!mandateDataRaw) {
        return {
          clinicStocks: [],
          clinicDet: {},
          services: [],
          locations: [],
          facilities: [],
        };
      }
      try {
        const reqData = JSON.parse(mandateDataRaw);
        return {
          clinicStocks: Array.isArray(reqData?.stocks) ? reqData.stocks : [],
          clinicDet: reqData?.clinic ?? {},
          services: Array.isArray(reqData?.services) ? reqData.services : [],
          locations: Array.isArray(reqData?.locations) ? reqData.locations : [],
          facilities: Array.isArray(reqData?.facilities)
            ? reqData.facilities
            : [],
        };
      } catch {
        return {
          clinicStocks: [],
          clinicDet: {},
          services: [],
          locations: [],
          facilities: [],
        };
      }
    }, [mandateDataRaw]);

  const getReqFacility = () =>
    facilities?.find((fac) => fac?.facilityId === appDet?.facilityId) ?? null;

  const getBatches = (stkId = null) => {
    const reqStock = clinicStocks?.find((stc) => stc?.stockId === stkId);
    const clinicBatches = reqStock?.stockBatch?.filter(
      (btch) => btch?.createdBy === clinicDet?.id,
    );
    const reqList = clinicBatches
      ?.filter((bat) =>
        isBatchAllowedForFacility(bat, getReqFacility(), locations),
      )
      ?.filter((li) => li?.quantity > 0)
      ?.sort((a, b) => a?.quantity - b?.quantity);
    return reqList?.length > 0
      ? reqList?.map((bat) => {
          const reqLocation = locations?.find(
            (loc) => loc?.id === bat?.locationId,
          );
          return {
            name: bat?.batchNo,
            value: bat?.batchId,
            subunits: bat?.subunits,
            quantity: bat?.quantity,
            locationId: bat?.locationId,
            locationName: reqLocation?.locationName ?? "",
            uom: bat?.qtyMapName ?? "",
            packageUom: bat?.packageUom ?? "",
          };
        })
      : [
          {
            name: "Unknown",
            value: null,
            subunits: null,
            quantity: null,
            locationId: null,
            locationName: "",
            uom: "",
            packageUom: "",
          },
        ];
  };

  const getBatch = (stockId, batchId = null) => {
    if (!stockId || !batchId) return null;
    const reqStock = clinicStocks?.find((stc) => stc?.stockId === stockId);
    return (
      reqStock?.stockBatch
        ?.filter((btch) => btch?.createdBy === clinicDet?.id)
        ?.find((bat) => bat?.batchId === batchId) ?? null
    );
  };

  const buildBatchOption = (stkId, batchId = null, batchNo = null) => {
    const reqBat = batchId ? getBatch(stkId, batchId) : null;
    const reqLocation = locations?.find(
      (loc) => loc?.id === reqBat?.locationId,
    );
    return {
      name: batchNo ?? reqBat?.batchNo ?? "Unknown",
      value: batchId ?? reqBat?.batchId ?? null,
      subunits: reqBat?.subunits ?? null,
      quantity: reqBat?.quantity ?? null,
      locationId: reqBat?.locationId ?? null,
      locationName: reqLocation?.locationName ?? "",
      uom: reqBat?.qtyMapName ?? "",
      packageUom: reqBat?.packageUom ?? "",
    };
  };

  const getBatchesForSelect = (
    stkId = null,
    batchId = null,
    batchNo = null,
  ) => {
    const batchList = getBatches(stkId);
    if (!batchId && !batchNo) return batchList;

    const exists = batchList?.some(
      (bat) =>
        (batchId && String(bat?.value) === String(batchId)) ||
        (batchNo && bat?.name === batchNo),
    );
    if (exists) return batchList;

    const selectedOption = buildBatchOption(stkId, batchId, batchNo);
    if (
      batchList?.length === 1 &&
      batchList?.[0]?.name === "Unknown" &&
      !batchList?.[0]?.value
    ) {
      return [selectedOption];
    }
    return [selectedOption, ...(batchList ?? [])];
  };

  const getUOMListByStockId = (stkId = null) => {
    if (!stkId) return [];
    const stockMat = clinicStocks?.find((stc) => stc?.stockId === stkId);
    return (
      stockMat?.uomMappings
        ?.filter((ml) => ml?.allowSale === "Y" && ml?.show)
        ?.map((ml) => ({
          name: ml?.unit,
          value: ml?.unit,
          conversion: ml?.conversion,
          uomMappingId: ml?.uomMappingId ?? null,
        })) ?? []
    );
  };

  const getProcedureListAsDrop = () => {
    if (!reqDet?.procedures?.length) return [];
    const reqProcedures = reqDet.procedures
      .filter((li) => li?.serviceId && li?.procedureVariantId)
      .map((prcd) => ({
        name: prcd?.procedureName,
        value: prcd?.procedureName,
        serviceId: prcd?.serviceId,
        variantId: prcd?.procedureVariantId,
      }));
    reqProcedures.unshift({
      name: NOT_LINKED_TO_PROCEDURE,
      value: NOT_LINKED_TO_PROCEDURE,
      serviceId: null,
      variantId: null,
    });
    return reqProcedures;
  };

  const getAdminList = (addedStockIds = new Set()) => {
    const categories = [
      "deworming",
      "diet",
      "flea & tick treatment",
      "fluid therapy",
      "grooming & hygiene care",
      "medication",
      "nutrition and supplements",
      "other parasite treatment",
      "vaccines",
    ];
    return (clinicStocks ?? [])
      ?.filter(
        (dta) =>
          dta?.active === "Y" &&
          categories.includes(String(dta?.category ?? "").toLowerCase()),
      )
      ?.map((li) => {
        const batchList = getBatches(li?.stockId);
        const reqBatch = batchList?.find((bat) => bat?.value) ?? null;
        const requestFromPharmacy =
          !li?.autoConsumed && !batchList?.some((bat) => bat?.value != null);
        const alreadyAdded = addedStockIds.has(li?.stockId);
        const clinicBatches = (li?.stockBatch ?? []).filter(
          (btch) => btch?.createdBy === clinicDet?.id,
        );
        const totalQty = clinicBatches.reduce(
          (acc, obj) => acc + Number(obj?.quantity ?? 0),
          0,
        );
        return {
          name: li?.tradeName ?? li?.productName,
          value: li?.tradeName ?? li?.productName,
          stockId: li?.stockId,
          type: li?.category,
          subCategory: li?.subCategory ?? "",
          productName: li?.productName,
          tradeName: li?.tradeName,
          totalQty,
          batchId: reqBatch?.value ?? null,
          batchNo: reqBatch?.name ?? null,
          requestFromPharmacy,
          alreadyAdded,
          disabled: alreadyAdded,
        };
      });
  };

  const addedStockIds = useMemo(() => {
    const ids = new Set();
    (reqDet?.procedures ?? []).forEach((procedure) => {
      (procedure?.procedureDetails ?? []).forEach((det) => {
        if (det?.isDelete) return;
        if (
          String(det?.type ?? "").toLowerCase() === "administered" &&
          det?.stockId
        ) {
          ids.add(det.stockId);
        }
      });
    });
    selectedItems.forEach((item) => {
      if (item?.stockId) ids.add(item.stockId);
    });
    return ids;
  }, [reqDet?.procedures, selectedItems]);

  const adminList = useMemo(
    () => getAdminList(addedStockIds),
    [clinicStocks, clinicDet?.id, appDet?.facilityId, addedStockIds],
  );

  const resolveInitialBatchNumber = (
    stockId,
    batchId = null,
    batchNo = null,
  ) => {
    if (!stockId) return null;
    const batchList = getBatchesForSelect(stockId, batchId, batchNo);
    if (batchId || batchNo) {
      const match = batchList?.find(
        (bat) =>
          (batchId && String(bat?.value) === String(batchId)) ||
          (batchNo && bat?.name === batchNo),
      );
      if (match) return match;
      return buildBatchOption(stockId, batchId, batchNo);
    }
    return batchList?.[0] ?? null;
  };

  useEffect(() => {
    if (!open) return;
    setValues(initValues);
    setSelectedItems([]);
    setIsSaving(false);
  }, [open]);

  const onChangeValue = (name, value, addObj) => {
    setValues((prev) => {
      const reqObj = { ...prev, [name]: value };
      if (name === "plName" && addObj) {
        if (addObj?.alreadyAdded) return prev;
        reqObj.nameErr = false;
        reqObj.stockId = addObj?.stockId ?? null;
        reqObj.plName = value?.name ?? value?.value ?? value ?? null;
        reqObj.requestFromPharmacy = !!addObj?.requestFromPharmacy;
        if (addObj?.requestFromPharmacy) {
          reqObj.batchNumber = null;
          reqObj.batchId = null;
        } else {
          const batchNumber = resolveInitialBatchNumber(addObj?.stockId);
          reqObj.batchNumber = batchNumber;
          reqObj.batchId = batchNumber?.value ?? null;
        }
        const uomList = getUOMListByStockId(addObj?.stockId);
        reqObj.uom = uomList?.[0] ?? null;
        reqObj.dosage = "";
        reqObj.route = null;
      }
      if (name === "batchNumber") {
        reqObj.batchNumber = value;
        reqObj.batchId = value?.value ?? null;
      }
      if (name === "linkToProcedure") {
        reqObj.linkToProcedure = value;
        reqObj.serviceId = addObj?.serviceId ?? null;
        reqObj.variantId = addObj?.variantId ?? null;
      }
      return reqObj;
    });
  };

  const onApiCall = () => {
    if (appDet?.appointmentId) {
      dispatch(getPetsCompliantSummaryWoLoader(appDet?.appointmentId));
    }
  };

  const getLinkedProcedureName = (formValues) =>
    typeof formValues?.linkToProcedure === "object"
      ? formValues?.linkToProcedure?.value
      : formValues?.linkToProcedure;

  const resolveTargetProcedure = (formValues) => {
    const linkedProcedureName = getLinkedProcedureName(formValues);
    const isLinkedToProcedure = linkedProcedureName !== NOT_LINKED_TO_PROCEDURE;
    if (isLinkedToProcedure) {
      return (
        reqDet?.procedures?.find(
          (pl) => pl?.procedureName === linkedProcedureName,
        ) ?? null
      );
    }
    return reqDet?.procedures?.[0] ?? null;
  };

  const buildAdministerDetail = (formValues) => {
    const reqStock = clinicStocks?.find(
      (stc) => stc?.stockId === formValues?.stockId,
    );
    const selectedBatchId =
      formValues?.batchNumber?.value ?? formValues?.batchId ?? null;
    const reqBatDet = getBatch(formValues?.stockId, selectedBatchId);
    const uomList = getUOMListByStockId(formValues?.stockId);
    const matchedUom =
      formValues?.uom ??
      uomList?.find(
        (ul) =>
          ul?.uomMappingId === formValues?.uomId ||
          ul?.value === formValues?.uomName,
      );
    const batUomMatch = uomList?.find(
      (ul) =>
        ul?.uomMappingId === reqBatDet?.mapId ||
        ul?.value === reqBatDet?.uomMapName,
    );
    const linkedProcedureName = getLinkedProcedureName(formValues);
    const isLinkedToProcedure = linkedProcedureName !== NOT_LINKED_TO_PROCEDURE;
    const linkToProcedure = isLinkedToProcedure;
    const isNoAmount = formValues?.billedSeparately
      ? false
      : reqStock?.autoConsumed || !reqStock?.billable || linkToProcedure;

    const clinicBatches = reqStock?.stockBatch?.filter(
      (btch) => btch?.createdBy === clinicDet?.id,
    );
    const reqBat = reqStock?.autoConsumed
      ? clinicBatches?.[0]
      : selectedBatchId
        ? (clinicBatches?.find((bat) => bat?.batchId === selectedBatchId) ??
          clinicBatches?.[0])
        : clinicBatches?.[0];
    const currLocat = locations?.find((loc) => loc?.id === reqBat?.locationId);
    const stckUnitPrice =
      reqBat?.unitSellingPrice ?? clinicBatches?.[0]?.unitSellingPrice ?? 0;
    const batchConversion = Number(batUomMatch?.conversion ?? 1);
    const selectedConversion = Number(matchedUom?.conversion ?? 1);
    const itmPrice =
      batUomMatch?.uomMappingId === matchedUom?.uomMappingId
        ? Number(stckUnitPrice)
        : (Number(stckUnitPrice) * batchConversion) / selectedConversion;
    const reqUnitPrice = isNoAmount ? 0 : itmPrice;

    const selectedProc = reqDet?.procedures?.find(
      (pl) => pl?.procedureName === linkedProcedureName,
    );
    const serviceId = isLinkedToProcedure
      ? (selectedProc?.serviceId ?? formValues?.serviceId)
      : null;
    const reqServ = services?.find((ser) => ser?.serviceId === serviceId);
    const reqProced =
      selectedProc ??
      reqDet?.procedures?.find((pl) => pl?.serviceId === serviceId);
    const reqVar = reqServ?.serviceVariants?.find(
      (vnt) =>
        String(vnt?.variantId) ===
        String(reqProced?.procedureVariantId ?? formValues?.variantId),
    );
    const taxPerent =
      reqServ?.taxApplicability?.toLowerCase() === "yes"
        ? Number(reqBat?.cgst ?? 0) + Number(reqBat?.sgst ?? 0)
        : 0;
    const reqPercent = 100 + Number(taxPerent);
    const quantity = Number(formValues?.quantity) || 1;
    const reqPrice = Number(reqUnitPrice) * Number(quantity ?? 1);
    const unitPrice = Number(reqPrice) / (reqPercent / 100);
    const prdTaxValue = (taxPerent / 100) * Number(unitPrice);
    const nextDueDate =
      linkToProcedure &&
      reqStock?.category?.toLowerCase() === "vaccines" &&
      reqServ?.nextDueDate?.length > 0
        ? getDoneAtFromNextDueDate(reqServ?.nextDueDate)
        : null;

    const isRequest =
      formValues?.requestFromPharmacy ||
      (!reqStock?.autoConsumed && !selectedBatchId && !reqBat?.batchId);

    return {
      detailId: null,
      stockId: formValues?.stockId ?? null,
      batchId:
        selectedBatchId ?? (reqStock?.autoConsumed ? reqBat?.batchId : null),
      type: "Administered",
      quantity,
      status: isRequest ? "requested" : true,
      remarks: isRequest ? "Requested" : "Completed",
      estimatedCost: isNoAmount ? 0 : (reqPrice ?? 0),
      name: formValues?.plName,
      procedureName: formValues?.plName,
      dosage: formValues?.dosage || null,
      route: formValues?.route?.value ?? formValues?.route?.name ?? null,
      isDelete: false,
      serviceId,
      tax: isNoAmount ? 0 : Number(prdTaxValue ?? 0),
      total: isNoAmount ? 0 : Number(reqPrice ?? 0),
      mrpPrice: isNoAmount ? 0 : Number(reqUnitPrice ?? 0),
      procedureVariantId: reqVar?.variantId ?? reqProced?.procedureVariantId,
      patientWorkbook: true,
      mainProcedure: false,
      drugSchedule: reqServ?.drugSchedule ?? null,
      hsnSacCode: reqServ?.hsnSacCode ?? null,
      packageUom: reqBat?.salesUom ?? null,
      expiryDate: reqBat?.expiryDate ?? null,
      batchNo: formValues?.batchNumber?.name || reqBat?.batchNo || null,
      billable: reqStock?.billable,
      autoConsumed: reqStock?.autoConsumed,
      linked: linkToProcedure ? "Y" : "N",
      billSeparated: !!formValues?.billedSeparately,
      billType: reqStock?.autoConsumed
        ? currLocat?.category
          ? billingTabEnum?.[currLocat?.category]
          : "treatment"
        : "treatment",
      uomId: matchedUom?.uomMappingId ?? null,
      uomName: matchedUom?.value ?? null,
      nextDueDate,
      billing: isRequest,
      locationId:
        formValues?.batchNumber?.locationId ??
        reqBat?.locationId ??
        currLocat?.id ??
        null,
      locationName:
        formValues?.batchNumber?.locationName ??
        currLocat?.locationName ??
        null,
    };
  };

  const buildProcedureUpdateData = (reqProced, reqServ, reqVar, details) => {
    let tax = 0;
    let total = reqVar?.servicePrice ?? reqProced?.serviceCharge ?? 0;
    if (reqServ?.taxApplicability?.toLowerCase() === "yes") {
      tax +=
        (Number(reqVar?.servicePrice ?? 0) / 100) * Number(reqServ?.tax ?? 0);
      total += tax;
    }

    return {
      serviceCharge: reqProced?.serviceCharge,
      serviceType: reqProced?.serviceType,
      appointmentId: reqDet?.appointment?.appointmentId,
      procedureDetails: details,
      tax: Number(Number(tax) * Number(reqProced?.quantity ?? 1)),
      total: Number(Number(total) * Number(reqProced?.quantity ?? 1)),
      mrpPrice: Number(reqVar?.servicePrice ?? 0),
      procedureVariantId: reqVar?.variantId,
      quantity: Number(reqProced?.quantity ?? 1),
      hsnSacCode: reqServ?.hsnSacCode ?? null,
      paymentId: reqDet?.paymentId ?? null,
      doseInSeries: reqProced?.doseInSeries ?? null,
      serviceRemainderId: reqProced?.serviceRemainderId ?? null,
    };
  };

  const handleAdd = () => {
    if (!values?.stockId || !values?.plName) {
      setValues((prev) => ({ ...prev, nameErr: true }));
      return;
    }

    setSelectedItems((prev) => [...prev, { ...values, nameErr: false }]);
    setValues(initValues);
    dispatch(
      showSnackBar({
        message: "Item added successfully",
        type: "success",
      }),
    );
  };

  const handleDone = async () => {
    const hasCurrentItem = !!values?.stockId;
    const itemsToSave = hasCurrentItem
      ? [...selectedItems, { ...values, nameErr: false }]
      : selectedItems;

    if (itemsToSave.length === 0) {
      onClose();
      return;
    }

    const groups = new Map();
    itemsToSave.forEach((formValues) => {
      const reqProced = resolveTargetProcedure(formValues);
      if (!reqProced?.procedureId) return;
      const key = String(reqProced.procedureId);
      if (!groups.has(key)) {
        groups.set(key, { reqProced, formValuesList: [] });
      }
      groups.get(key).formValuesList.push(formValues);
    });

    if (groups.size === 0) return;

    setIsSaving(true);
    try {
      const results = await Promise.all(
        Array.from(groups.values()).map(({ reqProced, formValuesList }) => {
          const linkedProcedureName = getLinkedProcedureName(formValuesList[0]);
          const isLinkedToProcedure =
            linkedProcedureName !== NOT_LINKED_TO_PROCEDURE;
          const serviceId = isLinkedToProcedure
            ? (reqProced?.serviceId ?? formValuesList[0]?.serviceId)
            : reqProced?.serviceId;
          const reqServ = services?.find((ser) => ser?.serviceId === serviceId);
          const reqVar = (
            Array.isArray(reqServ?.serviceVariants)
              ? reqServ.serviceVariants
              : []
          ).find(
            (vnt) =>
              String(vnt?.variantId) ===
              String(
                reqProced?.procedureVariantId ?? formValuesList[0]?.variantId,
              ),
          );
          const procedureDetails = formValuesList.map((formValues) =>
            buildAdministerDetail(formValues),
          );

          return dispatch(
            updateProcedureByIdWoLoader({
              id: reqProced.procedureId,
              data: buildProcedureUpdateData(
                reqProced,
                reqServ,
                reqVar,
                procedureDetails,
              ),
            }),
          );
        }),
      );

      const allSucceeded = results.every((res) => !!res?.payload);
      setIsSaving(false);
      if (allSucceeded) {
        refreshClinicStocksToSession(dispatch);
        onApiCall();
        onClose();
      }
    } catch {
      setIsSaving(false);
    }
  };

  const reqStck = values?.stockId
    ? clinicStocks?.find((stc) => stc?.stockId === values?.stockId)
    : null;
  const dosagePackageUom = getRightPackageUom(
    getBatch(values?.stockId, values?.batchNumber?.value ?? values?.batchId)
      ?.packageUom,
    true,
  );
  const hideBillSeparately = false;

  const dosageField = (
    <CustomTextField
      fullWidth
      handleChange={(e) => onChangeValue("dosage", e?.target?.value)}
      value={values?.dosage}
      suffix={
        dosagePackageUom?.length > 0 ? (
          <Custom3dotString
            str={`${dosagePackageUom}`}
            length={9}
            className="fs12 font-medium"
            capitalize={false}
          />
        ) : (
          ""
        )
      }
    />
  );

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      header="Add administered items"
      modal
      modalWidth={40}
      reduceHeaderSize
      hideClose
    >
      <Box
        display="flex"
        flexDirection="column"
        sx={{ height: "calc(100vh - 75px)" }}
      >
        <Box p={3} flexGrow={1} overflow="auto">
          <Box mb={3}>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                color: "#616161",
                letterSpacing: "0.5px",
                mb: 1,
                textTransform: "uppercase",
              }}
            >
              ITEM
            </Typography>
            <SearchableSelect
              variant="procedure"
              list={adminList ?? []}
              value={values?.plName}
              error={values?.nameErr}
              helperText={values?.nameErr ? "Required" : ""}
              plusBtn
              mandatoryField
              isForProcedure
              handleChange={(e, value) => {
                if (value?.alreadyAdded) return;
                onChangeValue("plName", value, {
                  stockId: value?.stockId ?? null,
                  requestFromPharmacy: !!value?.requestFromPharmacy,
                  alreadyAdded: !!value?.alreadyAdded,
                });
              }}
              maxFilteredOptions={100}
              placeholder="Search inventory by item name..."
            />
          </Box>

          <Grid container spacing={2} mb={3}>
            <Grid item xs={6}>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  color: "#616161",
                  letterSpacing: "0.5px",
                  mb: 1,
                  textTransform: "uppercase",
                }}
              >
                BATCH / LOT NO.
              </Typography>
              <CustomSelect
                list={getBatchesForSelect(
                  values?.stockId,
                  values?.batchNumber?.value ?? values?.batchId,
                  values?.batchNumber?.name,
                )}
                value={values?.batchNumber}
                handleChange={(e, value) => onChangeValue("batchNumber", value)}
                name="batchNumber"
                search
                isBatchNumber
                disabled={!values?.stockId}
              />
            </Grid>
            <Grid item xs={3}>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  color: "#616161",
                  letterSpacing: "0.5px",
                  mb: 1,
                  textTransform: "uppercase",
                }}
              >
                BILLABLE QTY
              </Typography>
              <TextField
                type="number"
                fullWidth
                size="small"
                value={values?.quantity}
                onChange={(e) => onChangeValue("quantity", e.target.value)}
                inputProps={{ min: 1 }}
                sx={{
                  backgroundColor: "#fff",
                  "& .MuiOutlinedInput-root": { borderRadius: "6px" },
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  color: "#616161",
                  letterSpacing: "0.5px",
                  mb: 1,
                  textTransform: "uppercase",
                }}
              >
                UOM
              </Typography>
              <CustomSelect
                list={getUOMListByStockId(values?.stockId)}
                value={values?.uom}
                search
                handleChange={(e, value) => onChangeValue("uom", value)}
                disabled={!values?.stockId}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} mb={3}>
            <Grid item xs={6}>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  color: "#616161",
                  letterSpacing: "0.5px",
                  mb: 1,
                  textTransform: "uppercase",
                }}
              >
                DOSE
              </Typography>
              {values?.stockId ? (
                <Tooltip
                  title={
                    <>
                      <Typography className="fs12" display="block">
                        Approximated Dosage: {reqStck?.dosage ?? "-"}
                      </Typography>
                      <Typography className="fs12" display="block">
                        Dosage Type: {reqStck?.dosageType ?? "-"}
                      </Typography>
                    </>
                  }
                >
                  <span style={{ display: "inline-block", width: "100%" }}>
                    {dosageField}
                  </span>
                </Tooltip>
              ) : (
                dosageField
              )}
            </Grid>
            <Grid item xs={6}>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  color: "#616161",
                  letterSpacing: "0.5px",
                  mb: 1,
                  textTransform: "uppercase",
                }}
              >
                ROUTE
              </Typography>
              <CustomSelect
                list={routeList}
                value={values?.route}
                search
                handleChange={(e, value) => onChangeValue("route", value)}
              />
            </Grid>
          </Grid>

          <Box mb={3}>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                color: "#616161",
                letterSpacing: "0.5px",
                mb: 1,
                textTransform: "uppercase",
              }}
            >
              LINK TO PROCEDURE
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              value={
                typeof values?.linkToProcedure === "object"
                  ? values?.linkToProcedure?.value
                  : values?.linkToProcedure
              }
              onChange={(e) => {
                const selected = getProcedureListAsDrop().find(
                  (opt) => opt.value === e.target.value,
                );
                onChangeValue("linkToProcedure", e.target.value, {
                  serviceId: selected?.serviceId,
                  variantId: selected?.variantId,
                });
              }}
              sx={{
                backgroundColor: "#fff",
                "& .MuiOutlinedInput-root": { borderRadius: "6px" },
              }}
            >
              {getProcedureListAsDrop().map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.name}
                </MenuItem>
              ))}
            </TextField>
            <Typography sx={{ fontSize: "12px", color: "#757575", mt: 0.8 }}>
              This is a clinical record of which procedure the item was used
              for.
            </Typography>
          </Box>

          {!hideBillSeparately ? (
            <Box mb={2}>
              <div className="flex-row">
                <Checkbox
                  checked={!!values?.billedSeparately}
                  onChange={(e) =>
                    onChangeValue("billedSeparately", e.target.checked)
                  }
                  sx={{
                    color: AppColors.appPrimary,
                    "&.Mui-checked": { color: AppColors.appPrimary },
                  }}
                />
                <div className="ml10">
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#212121",
                      mt: -0.3,
                    }}
                  >
                    Bill separately (extra line item)
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: "#757575" }}>
                    Suggested automatically — override if this case needs
                    something different.
                  </Typography>
                </div>
              </div>
            </Box>
          ) : null}
        </Box>

        <Box
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          gap={1.5}
          px={3}
          py={2}
          borderTop="1px solid #e0e0e0"
          backgroundColor="#fafafa"
        >
          <CustomButton
            text="Add to administered"
            smallBtn
            disabled={isSaving}
            onClick={handleAdd}
          />
          <Button
            onClick={handleDone}
            variant="outlined"
            disabled={isSaving}
            sx={{
              color: AppColors.appPrimary || "#0054A6",
              borderColor: AppColors.appPrimary || "#0054A6",
              textTransform: "none",
              fontWeight: 600,
              px: 2.5,
              height: "32px",
            }}
          >
            Done
          </Button>
        </Box>
      </Box>
    </CustomModal>
  );
};

export default AddAdministerDrawer;
